import type { ApiResponse } from '@shared/api'

import { useProfileStore } from '@entities/viewer'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface OAuthDisconnectReturn {
  isRunning: Ref<boolean>
  canDisconnect: ComputedRef<boolean>
  disconnect: (id: string) => Promise<boolean>
}

export const useOAuthDisconnect = (): OAuthDisconnectReturn => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const profileStore = useProfileStore()

  const isRunning = ref<boolean>(false)
  const canDisconnect = computed<boolean>(() => profileStore.privileges?.includes('manage-oauth') ?? false)

  const disconnect = async (id: string): Promise<boolean> => {
    const url = useAdminRoutes('admin.oauth.delete', { id })

    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')

      return false
    }

    isRunning.value = true

    try {
      const { data } = await $axios.delete<ApiResponse>(url)

      if (!data.success) {
        toast.error(data.message)

        return false
      }

      toast.success(data.message)

      return true
    } catch (err: unknown) {
      toast.error(getAxiosErrorMessage(err))

      return false
    } finally {
      isRunning.value = false
    }
  }

  return { isRunning, canDisconnect, disconnect }
}
